/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Derived status for the schedule's latest poll state.
 */
export const AIIntegrationScheduleStateStatus = {
  Pending: "pending",
  Success: "success",
  Failed: "failed",
  AutoPaused: "auto_paused",
  Disabled: "disabled",
} as const;
/**
 * Derived status for the schedule's latest poll state.
 */
export type AIIntegrationScheduleStateStatus = OpenEnum<
  typeof AIIntegrationScheduleStateStatus
>;

/**
 * Whether the stream carries discrete events or aggregated metrics. Omitted for legacy schedules with no registered stream.
 */
export const StreamKind = {
  Events: "events",
  Metrics: "metrics",
} as const;
/**
 * Whether the stream carries discrete events or aggregated metrics. Omitted for legacy schedules with no registered stream.
 */
export type StreamKind = OpenEnum<typeof StreamKind>;

/**
 * Scheduler state for one sync schedule (stream) of a provider integration.
 */
export type AIIntegrationScheduleState = {
  /**
   * ISO 8601 timestamp when the scheduler auto-paused the schedule after repeated provider rejections. Omitted unless auto-paused.
   */
  autoPausedAt?: Date | undefined;
  /**
   * Number of consecutive failed polls. Resets to zero on success or retry.
   */
  consecutiveFailures: number;
  /**
   * Whether the user has this schedule enabled. Disabled schedules are skipped by the poller until re-enabled.
   */
  enabled: boolean;
  /**
   * Stored error from the schedule's latest failed poll. Omitted unless the latest poll failed.
   */
  lastPollError?: string | undefined;
  /**
   * ISO 8601 timestamp for the schedule's latest failed poll. Omitted unless a poll has failed.
   */
  lastPollFailedAt?: Date | undefined;
  /**
   * ISO 8601 timestamp for the schedule's last successful poll. Omitted until a poll succeeds.
   */
  lastPollSuccessAt?: Date | undefined;
  /**
   * ISO 8601 timestamp for the schedule's next scheduled poll.
   */
  nextPollAfter?: Date | undefined;
  /**
   * Schedule identifier (e.g. cursor, anthropic_compliance, anthropic_analytics_usage).
   */
  schedule: string;
  /**
   * Derived status for the schedule's latest poll state.
   */
  status: AIIntegrationScheduleStateStatus;
  /**
   * Product-level identifier for the stream this schedule writes (e.g. cursor.usage, claude.chat.message). Omitted for legacy schedules with no registered stream.
   */
  stream?: string | undefined;
  /**
   * Whether the stream carries discrete events or aggregated metrics. Omitted for legacy schedules with no registered stream.
   */
  streamKind?: StreamKind | undefined;
};

/** @internal */
export const AIIntegrationScheduleStateStatus$inboundSchema: z.ZodMiniType<
  AIIntegrationScheduleStateStatus,
  unknown
> = openEnums.inboundSchema(AIIntegrationScheduleStateStatus);

/** @internal */
export const StreamKind$inboundSchema: z.ZodMiniType<StreamKind, unknown> =
  openEnums.inboundSchema(StreamKind);

/** @internal */
export const AIIntegrationScheduleState$inboundSchema: z.ZodMiniType<
  AIIntegrationScheduleState,
  unknown
> = z.pipe(
  z.object({
    auto_paused_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    consecutive_failures: z.int(),
    enabled: z.boolean(),
    last_poll_error: z.optional(z.string()),
    last_poll_failed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_poll_success_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    next_poll_after: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    schedule: z.string(),
    status: AIIntegrationScheduleStateStatus$inboundSchema,
    stream: z.optional(z.string()),
    stream_kind: z.optional(StreamKind$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "auto_paused_at": "autoPausedAt",
      "consecutive_failures": "consecutiveFailures",
      "last_poll_error": "lastPollError",
      "last_poll_failed_at": "lastPollFailedAt",
      "last_poll_success_at": "lastPollSuccessAt",
      "next_poll_after": "nextPollAfter",
      "stream_kind": "streamKind",
    });
  }),
);

export function aiIntegrationScheduleStateFromJSON(
  jsonString: string,
): SafeParseResult<AIIntegrationScheduleState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AIIntegrationScheduleState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AIIntegrationScheduleState' from JSON`,
  );
}
